<template>
  <div>
    <Breadcrumb :breadcrumb="breadcrumb"></Breadcrumb>
    <div class="flex-container">
      <span>
        <h5 class="title is-5">{{ $t("devices.titleUndetectedarea") }}</h5>
      </span>
      <ul>
        <li class="shape-item" @click="openHelpModal">
          <h5 class="title is-5">🔍 HELP</h5>
        </li>
      </ul>
    </div>
    <div class="container">
      <div class="canvas-container">
        <canvas
          ref="myCanvas"
          @mousedown="handleMouseDown"
          @mouseup="handleMouseUp"
          @click="handleClick"
          @mousemove="handleMouseMove"
        ></canvas>
        <div v-if="isLoading" id="loading-message">
          Loading<span v-html="loadingDots"></span>
        </div>
      </div>
      <div class="menu-container">
        <h5 class="title is-5">{{ $t("undetectedarea.menu") }}</h5>
        <ul>
          <li
            v-for="(shape, index) in shapes"
            :key="index"
            @click="handleMenuClick(index)"
            :class="['shape-item', { selected: index === selectedShapeIndex }]"
          >
            {{ $t("undetectedarea.figure") }} : {{ index + 1 }}
          </li>
          <li class="shape-item" @click="handleClickAdd(index)">
            <i class="fas fa-plus"></i>
            {{ $t("undetectedarea.addFigure") }}
          </li>
          <div>
            <button
              v-if="selectedShapeIndex !== -1"
              @click="handleClickDel(selectedShapeIndex)"
              class="button is-danger ml-3"
              v-bind:class="{ 'is-loading': isLoad }"
            >
              {{ $t("undetectedarea.delete") }}
            </button>
          </div>
          <br />
          <div>
            <button
              @click="onSubmit()"
              class="button is-primary ml-3"
              v-bind:class="{ 'is-loading': isLoad }"
              :disabled="drawingMode !== 'FIN'"
            >
              {{ $t("Confirm") }}
            </button>
          </div>
          <br />
        </ul>
      </div>
    </div>
  </div>
  <UndetectedAreaJa
    v-if="lang === 'ja'"
    :isHelpModalOpen="isHelpModalOpen"
    @close="isHelpModalOpen = false"
  />
  <UndetectedAreaEnus
    v-if="lang === 'en-US'"
    :isHelpModalOpen="isHelpModalOpen"
    @close="isHelpModalOpen = false"
  />
</template>

<script>
import Breadcrumb from "@/components/Breadcrumb";
import UndetectedAreaEnus from "@/components/devices/UndetectedAreaEnus.vue";
import UndetectedAreaJa from "@/components/devices/UndetectedAreaJa.vue";

import imsr from "@/imsr-api";
import { computed, onMounted, onUnmounted, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useRoute, useRouter } from "vue-router";
import { useStore } from "vuex";

export default {
  components: {
    Breadcrumb,
    UndetectedAreaJa,
    UndetectedAreaEnus,
  },
  setup() {
    const { locale } = useI18n();
    const route = useRoute();
    const myCanvas = ref(null);
    const canvas = ref(null);
    const ctx = ref(null);
    const image = ref(new Image());
    const points = ref([]);
    const shapes = ref([]);
    const draggingPointIndex = ref(-1);
    const drawingMode = ref("FIN");
    const isLoading = ref(true);
    const isLoad = ref(false);
    const loadingDots = ref("");
    const store = useStore();
    const router = useRouter();
    const selectedShapeIndex = ref(-1);
    const breadcrumb = ref([
      {
        title: "Home",
        path: {
          name: "dashboard",
          params: { projectId: route.params.projectId },
        },
      },
      {
        title: "Device Config",
        path: {
          name: "devicesList",
          params: { projectId: route.params.projectId },
        },
      },
      {
        title: route.params.deviceId,
        path: {
          name: "devicesView",
          params: {
            projectId: route.params.projectId,
            deviceId: route.params.deviceId,
          },
        },
      },
      { title: "undetectedArea", path: "#", isActive: true },
    ]);
    const onSubmit = async () => {
      try {
        isLoad.value = true;
        const newShapes = shapes.value;
        for (let i = 0; i < newShapes.length; i++) {
          for (let j = 0; j < newShapes[i].length; j++) {
            delete newShapes[i][j].isNew;
          }
        }

        const values = {
          tenantId: store.getters.tenantId,
          projectId: route.params.projectId,
          deviceId: route.params.deviceId,
          undetectedarea: {
            height: canvas.value.height,
            width: canvas.value.width,
            shape: newShapes,
          },
        };
        await imsr.imsrPutApi("/devices/undetectedarea/", values);
        isLoad.value = false;
        isHelpModalOpen.value = false;
        await router.push({
          name: "devicesView",
          params: {
            projectId: route.params.projectId,
            deviceId: route.params.deviceId,
          },
        });
      } catch (e) {
        router.push({ name: "InternalServerError" });
      }
    };

    const findPointIndex = (x, y, list) => {
      for (let i = 0; i < list.length; i++) {
        const point = list[i];
        const distance = Math.sqrt(
          Math.pow(x - point.x, 2) + Math.pow(y - point.y, 2)
        );
        if (distance <= 10) {
          return i;
        }
      }
      return -1;
    };

    const getMousePos = (event) => {
      const rect = canvas.value.getBoundingClientRect();
      const scaleX = canvas.value.width / rect.width;
      const scaleY = canvas.value.height / rect.height;
      const x = (event.clientX - rect.left) * scaleX;
      const y = (event.clientY - rect.top) * scaleY;
      return { x, y };
    };

    const drawImageAndShapes = (nowPoints) => {
      ctx.value.clearRect(0, 0, canvas.value.width, canvas.value.height);
      ctx.value.drawImage(
        image.value,
        0,
        0,
        canvas.value.width,
        canvas.value.height
      );
      const drawShape = (shapePoints) => {
        ctx.value.beginPath();
        ctx.value.moveTo(shapePoints[0].x, shapePoints[0].y);
        for (let i = 1; i < shapePoints.length; i++) {
          ctx.value.lineTo(shapePoints[i].x, shapePoints[i].y);
        }
        ctx.value.closePath();
        if (shapePoints[0].isNew) {
          ctx.value.fillStyle = "rgba(255, 0, 0, 0.3)";
        } else {
          ctx.value.fillStyle = "rgba(0, 0, 255, 0.3)";
        }
        ctx.value.fill();
      };

      for (const shape of shapes.value) {
        if (Object.keys(shape).length !== 0) {
          drawShape(shape);
        }
      }

      for (let i = 0; i < nowPoints.length; i++) {
        const point = nowPoints[i];
        if (i === 0) {
          ctx.value.beginPath();
          ctx.value.arc(point.x, point.y, 7.5, 0, 2 * Math.PI);
          ctx.value.fillStyle = "rgba(0, 0, 255, 1)";
          ctx.value.fill();
          ctx.value.strokeStyle = "white";
          ctx.value.lineWidth = 1.5;
          ctx.value.stroke();
        } else {
          ctx.value.beginPath();
          ctx.value.arc(point.x, point.y, 5, 0, 2 * Math.PI);
          ctx.value.fillStyle = "white";
          ctx.value.fill();
          ctx.value.strokeStyle = "black";
          ctx.value.lineWidth = 1;
          ctx.value.stroke();
        }
      }
    };

    const handleMouseDown = (event) => {
      const { x, y } = getMousePos(event);
      draggingPointIndex.value = findPointIndex(x, y, points.value);
    };

    const handleMouseUp = () => {
      draggingPointIndex.value = -1;
      if (drawingMode.value === "EDIT") {
        shapes.value[selectedShapeIndex] = Array.from(points.value);
      }
    };

    const handleClick = (event) => {
      const { x, y } = getMousePos(event);
      const clickedPointIndex = findPointIndex(x, y, points.value);
      if (drawingMode.value === "ADD") {
        if (clickedPointIndex === 0 && points.value.length > 2) {
          shapes.value[shapes.value.length - 1] = Array.from(points.value);
          points.value.length = 0;
          drawingMode.value = "FIN";
          return;
        }
        if (clickedPointIndex === -1) {
          points.value.push({
            x: x,
            y: y,
            id: points.value.length + 1,
            isNew: true,
          });
        }
      }
    };

    const handleMouseMove = (event) => {
      const { x: mouseX, y: mouseY } = getMousePos(event);
      let overLayColor = "rgba(0, 0, 255, 0.3)";

      if (draggingPointIndex.value !== -1) {
        points.value[draggingPointIndex.value].x = mouseX;
        points.value[draggingPointIndex.value].y = mouseY;
      }

      const mousePointIndex = findPointIndex(mouseX, mouseY, points.value);
      if (mousePointIndex == 0) {
        overLayColor = "rgba(255, 0, 0, 0.3)";
      } else {
        overLayColor = "rgba(255, 0, 0, 0.1)";
      }
      drawImageAndShapes(points.value);

      if (points.value.length > 0 && drawingMode.value === "ADD") {
        ctx.value.fillStyle = overLayColor;
        ctx.value.beginPath();
        ctx.value.moveTo(points.value[0].x, points.value[0].y);
        for (let i = 0; i < points.value.length; i++) {
          ctx.value.lineTo(points.value[i].x, points.value[i].y);
        }
        ctx.value.lineTo(mouseX, mouseY);
        ctx.value.closePath();
        ctx.value.fill();

        ctx.value.beginPath();
        ctx.value.strokeStyle = overLayColor;
        ctx.value.moveTo(points.value[0].x, points.value[0].y);
        ctx.value.lineTo(mouseX, mouseY);
        ctx.value.lineTo(
          points.value[points.value.length - 1].x,
          points.value[points.value.length - 1].y
        );
        ctx.value.stroke();
      }
    };

    const updateLoadingDots = () => {
      const dotCount = (loadingDots.value.length % 3) + 1;
      loadingDots.value = "・".repeat(dotCount);
    };

    const loadingInterval = setInterval(updateLoadingDots, 500);

    onUnmounted(() => {
      clearInterval(loadingInterval);
    });

    const getImages = async () => {
      try {
        let image1 = "/img/blank.png";

        let params = {
          tenantId: store.getters.tenantId,
          projectId: route.params.projectId,
          deviceId: route.params.deviceId,
          imageType: "img03",
        };
        const img1 = await imsr.imsrGetImageApi(params);
        if (img1.file == "true") {
          image1 = "data:image/png;base64," + img1.rawData;
        }
        return image1;
      } catch (e) {
        router.push({ name: "InternalServerError" });
      }
    };

    const handleMenuClick = (index) => {
      points.value = shapes.value[index];
      selectedShapeIndex.value = index;
      drawingMode.value = "EDIT";
      drawImageAndShapes(points.value);
    };

    const handleClickAdd = () => {
      if (drawingMode.value != "ADD") {
        if (selectedShapeIndex.value != -1) {
          shapes.value[selectedShapeIndex] = Array.from(points.value);
        }
        shapes.value.push(Array.from({}));
        drawingMode.value = "ADD";
        selectedShapeIndex.value = shapes.value.length - 1;
        points.value = shapes.value[selectedShapeIndex.value];
        drawImageAndShapes(points.value);
      }
    };

    const handleClickDel = (index) => {
      shapes.value.splice(index, 1);
      drawingMode.value = "FIN";
      selectedShapeIndex.value = -1;
      points.value.length = 0;
      drawImageAndShapes(points.value);
    };

    onMounted(() => {
      const init = async () => {
        canvas.value = myCanvas.value;
        ctx.value = canvas.value.getContext("2d");
        image.value.src = await getImages();
        shapes.value = JSON.parse(route.params.undetectedArea).shape;
        image.value.onload = () => {
          clearInterval(loadingInterval);
          canvas.value.width = image.value.naturalWidth / 2;
          canvas.value.height = image.value.naturalHeight / 2;
          drawImageAndShapes(points.value);

          onUnmounted(() => clearInterval(loadingInterval));
        };
        isLoading.value = false;
      };

      init();
    });

    const isHelpModalOpen = ref(false);

    const openHelpModal = () => {
      isHelpModalOpen.value = true;
    };

    const closeHelpModal = () => {
      isHelpModalOpen.value = false;
    };

    return {
      breadcrumb,
      myCanvas,
      handleMouseDown,
      handleMouseUp,
      handleClick,
      handleMouseMove,
      isLoading,
      loadingDots,
      isLoad,
      shapes,
      onSubmit,
      handleMenuClick,
      selectedShapeIndex,
      handleClickAdd,
      handleClickDel,
      isHelpModalOpen,
      openHelpModal,
      closeHelpModal,
      drawingMode,
      lang: computed(() => {
        return locale.value;
      }),
    };
  },
};
</script>
<style>
.flex-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.flex-container i {
  margin-right: 10px;
}
#loading-message {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  font-size: 24px;
}
.container {
  display: flex;
  justify-content: space-between;
}
.canvas-container {
  flex: 1;
}
.menu-container {
  width: 250px;
  padding: 10px;
  border-left: 1px solid #ddd; /* 境界線 */
}
.menu-container h5 {
  margin-bottom: 10px;
}
.menu-container ul {
  width: 250px;
}
.menu-container clicked {
  background-color: grey;
}
.menu-container button {
  width: 250px;
}
.shape-item {
  padding: 10px;
  margin-bottom: 5px;
  margin-left: 10px;
  background-color: #ffffff;
  border: 1px solid #fffdfd;
  cursor: pointer;
  transition: background-color 0.3s;
}

.selected {
  background-color: #b0e0e6;
}

.shape-item:hover {
  background-color: #e0e0e0;
  color: blue;
}
</style>
